import React from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import dayjs from 'dayjs'
import { IPriceHistoryItem, IPriceHistoryProduct } from 'models/product'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import styles from './PriceHistory.module.scss'

const SERIES_COLOR = '#2a78d6'
const SURFACE_COLOR = '#ffffff'
const GRID_COLOR = '#ececec'
const AXIS_TEXT_COLOR = '#8c8c8c'

interface IPricePoint {
  time: number
  price: number
  // точка смены цены — с маркером; служебные точки (начало, «сейчас») без него
  isChange: boolean
  isNow?: boolean
}

const X_TICK_COUNT = 5
const Y_TICK_COUNT = 4
const NICE_STEPS = [1, 2, 2.5, 5, 10]

// Цена держится до следующей смены: ступенька от записи к записи и до сегодняшнего дня
const buildPoints = (
  product: IPriceHistoryProduct,
  items: IPriceHistoryItem[]
): IPricePoint[] => {
  const changes = items
    .filter((item) => item.new_price != null)
    .sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf())

  const points: IPricePoint[] = []
  if (!changes.length) {
    points.push({
      time: dayjs(product.createdAt).valueOf(),
      price: product.price,
      isChange: false
    })
  }
  changes.forEach((item, index) => {
    const time = dayjs(item.createdAt).valueOf()
    if (index === 0 && item.old_price != null) {
      points.push({ time, price: item.old_price, isChange: false })
    }
    points.push({ time, price: item.new_price as number, isChange: true })
  })
  points.push({
    time: Date.now(),
    price: product.price,
    isChange: false,
    isNow: true
  })
  return points
}

interface IPriceTooltipProps {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: IPricePoint }>
}

// Деления задаём сами: по умолчанию ось ставит их в точки данных,
// а у первой смены цены две точки с одним временем («было» и «стало»)
const getTimeTicks = (points: IPricePoint[]) => {
  const start = points[0].time
  const end = points[points.length - 1].time
  if (end <= start) return [start]
  const step = (end - start) / (X_TICK_COUNT - 1)
  return Array.from({ length: X_TICK_COUNT }, (_, index) =>
    Math.round(start + step * index)
  )
}

// Круглые деления цены; запас 5% нужен, иначе при неизменной цене ось схлопывается в точку
const getPriceAxis = (points: IPricePoint[]) => {
  const prices = points.map((point) => point.price)
  const padding = Math.max(...prices) * 0.05 || 1
  const min = Math.max(0, Math.min(...prices) - padding)
  const max = Math.max(...prices) + padding
  const rawStep = (max - min) / Y_TICK_COUNT
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const step =
    magnitude *
    (NICE_STEPS.find((nice) => nice * magnitude >= rawStep) ?? 10)
  const start = Math.floor(min / step) * step
  const end = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let tick = start; tick <= end + step / 2; tick += step) {
    ticks.push(Math.round(tick))
  }
  return { domain: [start, end] as [number, number], ticks }
}

const PriceTooltip = ({ active, payload }: IPriceTooltipProps) => {
  const point = payload?.[0]?.payload
  if (!active || !point) return null
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipValue}>{getNormalizedPrice(point.price)}</div>
      <div className={styles.tooltipLabel}>
        {point.isNow ? 'Сейчас' : dayjs(point.time).format('DD.MM.YYYY HH:mm')}
      </div>
    </div>
  )
}

interface IPriceHistoryChartProps {
  product: IPriceHistoryProduct
  items: IPriceHistoryItem[]
}

export const PriceHistoryChart: React.FC<IPriceHistoryChartProps> = ({
  product,
  items
}) => {
  const points = React.useMemo(
    () => buildPoints(product, items),
    [product, items]
  )
  const timeTicks = React.useMemo(() => getTimeTicks(points), [points])
  const priceAxis = React.useMemo(() => getPriceAxis(points), [points])

  return (
    <div className={styles.chart}>
      <div className={styles.chartTitle}>Цена для покупателя</div>
      <ResponsiveContainer width='100%' height={220}>
        <LineChart data={points} margin={{ top: 8, right: 32, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={GRID_COLOR} vertical={false} />
          <XAxis
            dataKey='time'
            type='number'
            scale='time'
            domain={['dataMin', 'dataMax']}
            ticks={timeTicks}
            tickFormatter={(time: number) => dayjs(time).format('DD.MM.YY')}
            tick={{ fill: AXIS_TEXT_COLOR, fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: GRID_COLOR }}
            minTickGap={24}
          />
          <YAxis
            domain={priceAxis.domain}
            ticks={priceAxis.ticks}
            allowDecimals={false}
            tickFormatter={(price: number) => price.toLocaleString('ru-RU')}
            tick={{ fill: AXIS_TEXT_COLOR, fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            width={64}
          />
          <Tooltip
            content={({ active, payload }) => (
              <PriceTooltip active={active} payload={payload} />
            )}
            cursor={{ stroke: AXIS_TEXT_COLOR, strokeWidth: 1 }}
            isAnimationActive={false}
          />
          <Line
            type='stepAfter'
            dataKey='price'
            stroke={SERIES_COLOR}
            strokeWidth={2}
            strokeLinejoin='round'
            strokeLinecap='round'
            isAnimationActive={false}
            dot={({ cx, cy, index, payload }) =>
              payload.isChange ? (
                <circle
                  key={index}
                  cx={cx}
                  cy={cy}
                  r={4}
                  fill={SERIES_COLOR}
                  stroke={SURFACE_COLOR}
                  strokeWidth={2}
                />
              ) : (
                <g key={index} />
              )
            }
            activeDot={{
              r: 5,
              fill: SERIES_COLOR,
              stroke: SURFACE_COLOR,
              strokeWidth: 2
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
